import React from 'react';
import { Link } from 'react-router-dom';
import { SupportLayout } from './SupportLayout';

interface ResourceCardProps {
  title: string;
  description: string;
  to: string;
  external?: boolean;
}

const ResourceCard: React.FC<ResourceCardProps> = ({ title, description, to, external }) => {
  const content = (
    <div className="block p-6 border border-gray-200 rounded-2xl hover:border-primary-300 hover:shadow-md transition-all h-full">
      <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  );
  return external ? (
    <a href={to} target="_blank" rel="noopener noreferrer">
      {content}
    </a>
  ) : (
    <Link to={to}>{content}</Link>
  );
};

export const HostResourcesPage: React.FC = () => {
  return (
    <SupportLayout
      title="Host resources"
      subtitle="Everything you need to list well, get bookings, and keep guests happy."
    >
      <h2 className="text-2xl font-semibold text-gray-900 mb-4">Getting started</h2>
      <div className="grid sm:grid-cols-2 gap-4 mb-10">
        <ResourceCard
          title="Become a host"
          description="The 20-minute setup: photos, dates, pricing. We come visit, then you go live."
          to="/become-host"
        />
        <ResourceCard
          title="Host terms of service"
          description="What you agree to as a host on DARAK."
          to="/host-terms"
        />
        <ResourceCard
          title="Community guidelines"
          description="Standards we expect from every host on the platform."
          to="/host-guidelines"
        />
        <ResourceCard
          title="Responsible hosting"
          description="Local laws, taxes, neighbors, and how to keep everyone happy."
          to="/host/responsible-hosting"
        />
      </div>

      <h2 className="text-2xl font-semibold text-gray-900 mb-4">Pricing your place</h2>
      <ul className="space-y-3 text-gray-700 mb-10">
        <li>
          <strong className="text-gray-900">Look at comparable listings.</strong> Open the search page, filter by
          your city and property type, and price within 10% of the median for similar places.
        </li>
        <li>
          <strong className="text-gray-900">Charge more on weekends.</strong> Set a separate weekend rate (Friday
          and Saturday nights). Most hosts charge 20-40% more.
        </li>
        <li>
          <strong className="text-gray-900">Adjust by season.</strong> Faraya peaks in winter; Batroun and Byblos
          peak May to September. Use the calendar to set custom rates per night.
        </li>
        <li>
          <strong className="text-gray-900">Lower your minimum stay.</strong> A 1-night minimum gets you more
          bookings than a 3-night minimum, especially mid-week.
        </li>
      </ul>

      <h2 className="text-2xl font-semibold text-gray-900 mb-4">Photos that book</h2>
      <ul className="space-y-3 text-gray-700 mb-10">
        <li>Wide shots of every room, taken in daylight.</li>
        <li>The view from your terrace or window. Lebanese guests scroll for views.</li>
        <li>The bed, made. The kitchen, clean. The bathroom, dry.</li>
        <li>15+ photos minimum. Listings with under 10 get half the bookings.</li>
      </ul>

      <h2 className="text-2xl font-semibold text-gray-900 mb-4">Once you're live</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <ResourceCard
          title="Host dashboard"
          description="Your bookings, calendar, earnings, and messages in one place."
          to="/host"
        />
        <ResourceCard
          title="Calendar"
          description="Block dates, set seasonal pricing, sync to Airbnb or Booking.com via iCal."
          to="/host/calendar"
        />
      </div>

      <div className="mt-12 p-6 bg-primary-50 rounded-2xl">
        <p className="text-gray-900 font-medium mb-2">Stuck on something specific?</p>
        <p className="text-gray-600 mb-4">
          The Beirut team has helped 150+ hosts list. Email us with your question and we'll usually reply same-day.
        </p>
        <Link
          to="/contact"
          className="inline-block px-6 py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors"
        >
          Email the team
        </Link>
      </div>
    </SupportLayout>
  );
};

export default HostResourcesPage;
